// src/pages/BI/MindMapEditor.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  addEdge,
  Connection,
  Node,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
  BackgroundVariant,
  Edge,
  EdgeProps,
  getBezierPath,
  ConnectionLineType,
  useStore,
  NodeTypes,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  ArrowLeft, Save, Download, ZoomIn, ZoomOut, Maximize,
  FileJson, Upload, Plus, Minus, Minimize, Trash2, Trash, HelpCircle,
  Menu, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getMindMap, createMindMap, updateMindMap } from '../api/mindmaps';
import BILayout from '../components/BILayout';
import ReactDOM from 'react-dom';
import { snapdom } from '@zumer/snapdom';

// ==================== КОНСТАНТЫ ====================
const presetColors = [
  '#7B2FF7', '#00E5FF', '#FF4D4F', '#FAAD14', '#52C41A',
  '#EB2F96', '#FF8A80', '#80D8FF', '#B9F6CA', '#EA80FC',
  '#FFD93D', '#84FFFF', '#ffffff', '#f8f9fa'
];

const NODE_SIZES = [
  { label: 'S', width: 90, fontSize: 12, padding: '8px 14px' },
  { label: 'M', width: 130, fontSize: 14, padding: '12px 20px' },
  { label: 'L', width: 170, fontSize: 16, padding: '16px 24px' },
  { label: 'XL', width: 210, fontSize: 18, padding: '20px 28px' },
];

interface CustomNodeData {
  label: string;
  backgroundColor: string;
  sizeIndex: number;
  onLabelChange: (id: string, label: string) => void;
  onColorChange?: (id: string, color: string) => void;
  onSizeChange?: (id: string, sizeIndex: number) => void;
  onDelete?: (id: string) => void;
}

interface CustomEdgeData {
  label?: string;
  strokeWidth?: number;
  stroke?: string;
  onLabelChange?: (edgeId: string, label: string) => void;
  onStyleChange?: (edgeId: string, stroke: string, strokeWidth: number) => void;
  onDeleteEdge?: (edgeId: string) => void;
}

// ==================== КАСТОМНАЯ СВЯЗЬ – ПЛАВНЫЕ КРИВЫЕ БЕЗЬЕ ====================
const CustomEdge: React.FC<EdgeProps<CustomEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelText, setLabelText] = useState(data?.label || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingLabel(true);
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const handleBlur = () => {
    if (labelText.trim() && data?.onLabelChange) data.onLabelChange(id, labelText);
    setIsEditingLabel(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setLabelText(data?.label || '');
      setIsEditingLabel(false);
    }
  };

  const strokeWidth = data?.strokeWidth || (style as any).strokeWidth || 2;
  const strokeColor = data?.stroke || (style as any).stroke || '#7B2FF7';

  const handleDeleteEdge = () => data?.onDeleteEdge?.(id);

  return (
    <>
      <path
        id={id}
        style={{
          ...style,
          stroke: strokeColor,
          strokeWidth: strokeWidth,
          strokeLinecap: 'round',
        }}
        className="react-flow__edge-path"
        d={edgePath}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => {
          e.preventDefault();
          if (data?.onStyleChange) {
            const rect = (e.target as HTMLElement).getBoundingClientRect();
            const customEvent = new CustomEvent('openEdgeContextMenu', {
              detail: {
                edgeId: id,
                x: rect.right + 10,
                y: rect.top,
                stroke: strokeColor,
                strokeWidth,
                onDelete: handleDeleteEdge
              }
            });
            window.dispatchEvent(customEvent);
          }
        }}
        onDoubleClick={handleDoubleClick}
      />
      {isEditingLabel ? (
        <foreignObject
          width={150}
          height={30}
          x={labelX - 75}
          y={labelY - 15}
          style={{ overflow: 'visible' }}
        >
          <input
            ref={inputRef}
            type="text"
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={{
              width: '100%',
              padding: '4px 8px',
              border: '2px solid #7B2FF7',
              borderRadius: '20px',
              fontSize: '12px',
              textAlign: 'center',
              background: 'rgba(255,255,255,0.9)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              backdropFilter: 'blur(8px)',
              color: '#1a1a2e',
              outline: 'none'
            }}
            autoFocus
          />
        </foreignObject>
      ) : data?.label ? (
        <foreignObject
          width={150}
          height={20}
          x={labelX - 75}
          y={labelY - 10}
          style={{ overflow: 'visible', pointerEvents: 'none' }}
        >
          <div
            style={{
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(8px)',
              padding: '2px 10px',
              borderRadius: '20px',
              fontSize: '11px',
              color: '#1a1a2e',
              textAlign: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              pointerEvents: 'auto',
              cursor: 'pointer',
              border: '1px solid rgba(255,255,255,0.5)',
            }}
            onDoubleClick={handleDoubleClick}
            onContextMenu={(e) => {
              e.preventDefault();
              const rect = (e.target as HTMLElement).getBoundingClientRect();
              const customEvent = new CustomEvent('openEdgeContextMenu', {
                detail: {
                  edgeId: id,
                  x: rect.right + 10,
                  y: rect.top,
                  stroke: strokeColor,
                  strokeWidth,
                  onDelete: handleDeleteEdge
                }
              });
              window.dispatchEvent(customEvent);
            }}
          >
            {data.label}
          </div>
        </foreignObject>
      ) : null}
    </>
  );
};

// ==================== КАСТОМНЫЙ УЗЕЛ (светлое стекло, размеры, без иконок) ====================
const CustomNode: React.FC<{ data: CustomNodeData; id: string; selected?: boolean }> = React.memo(({ data, id, selected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label || '');
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const color = data.backgroundColor || '#7B2FF7';
  const sizeIndex = data.sizeIndex ?? 1;
  const size = NODE_SIZES[sizeIndex] || NODE_SIZES[1];
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('top');
  const menuRef = useRef<HTMLDivElement>(null);

  const isConnecting = useStore(s => s.connectionNodeId !== null);
  const showHandles = hovered || selected || isConnecting;

  useEffect(() => {
    setLabel(data.label || '');
  }, [data.label]);

  useLayoutEffect(() => {
    if (selected && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      if (rect.top < 0) {
        setMenuPosition('bottom');
      } else {
        setMenuPosition('top');
      }
    }
  }, [selected]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const handleBlur = () => {
    if (label.trim() && data.onLabelChange) data.onLabelChange(id, label);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setLabel(data.label || '');
      setIsEditing(false);
    }
  };

  const handleColorSelect = (c: string) => {
    data.onColorChange?.(id, c);
  };

  const handleSizeSelect = (newSizeIndex: number) => {
    data.onSizeChange?.(id, newSizeIndex);
  };

  const handleDeleteNode = () => {
    data.onDelete?.(id);
  };

  const handleStyle = (visible: boolean): React.CSSProperties => ({
    background: color,
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.8)',
    boxShadow: visible ? `0 0 10px ${color}90` : 'none',
    opacity: visible ? 1 : 0,
    transform: visible ? 'scale(1)' : 'scale(0.4)',
    transition: 'opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
    pointerEvents: visible ? 'all' : 'none',
  });

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${color}30, ${color}10)`,
        backdropFilter: 'blur(12px)',
        borderRadius: 16,
        padding: size.padding,
        border: `2px solid ${selected ? '#fff' : hovered ? `${color}90` : `${color}40`}`,
        color: '#1a1a2e',
        fontSize: size.fontSize,
        fontWeight: 600,
        minWidth: size.width,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.25s, box-shadow 0.25s',
        boxShadow: selected
          ? `0 0 20px ${color}60, 0 4px 16px rgba(0,0,0,0.1)`
          : hovered
          ? `0 0 14px ${color}40, 0 4px 12px rgba(0,0,0,0.08)`
          : `0 4px 12px rgba(0,0,0,0.06)`,
        position: 'relative',
        zIndex: selected ? 10 : 1,
        userSelect: 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={handleDoubleClick}
      className="custom-node"
      data-node-id={id}
    >
      <Handle type="target" position={Position.Top} id="top-target" style={handleStyle(showHandles)} />
      <Handle type="source" position={Position.Top} id="top-source" style={handleStyle(showHandles)} />
      <Handle type="target" position={Position.Bottom} id="bottom-target" style={handleStyle(showHandles)} />
      <Handle type="source" position={Position.Bottom} id="bottom-source" style={handleStyle(showHandles)} />
      <Handle type="target" position={Position.Left} id="left-target" style={handleStyle(showHandles)} />
      <Handle type="source" position={Position.Left} id="left-source" style={handleStyle(showHandles)} />
      <Handle type="target" position={Position.Right} id="right-target" style={handleStyle(showHandles)} />
      <Handle type="source" position={Position.Right} id="right-source" style={handleStyle(showHandles)} />

      {isEditing ? (
        <textarea
          ref={inputRef}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            minWidth: '120px',
            border: '2px solid #7B2FF7',
            borderRadius: '12px',
            padding: '6px 10px',
            fontFamily: 'inherit',
            fontSize: size.fontSize,
            fontWeight: 600,
            color: '#1a1a2e',
            background: 'rgba(255,255,255,0.9)',
            resize: 'none',
            outline: 'none',
            lineHeight: '1.4',
            boxSizing: 'border-box',
            boxShadow: '0 4px 12px rgba(123, 47, 247, 0.3)',
          }}
          rows={Math.max(1, Math.ceil(label.length / 20))}
          autoFocus
        />
      ) : (
        <span>{label || 'Новый узел'}</span>
      )}

      {selected && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute',
            [menuPosition === 'top' ? 'top' : 'bottom']: menuPosition === 'top' ? -40 : 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 4,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(12px)',
            borderRadius: 10,
            padding: '4px 8px',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 20,
            whiteSpace: 'nowrap',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {presetColors.slice(0, 12).map(c => (
            <div
              key={c}
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: c,
                border: data.backgroundColor === c ? '2px solid #7B2FF7' : '1px solid rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'transform 0.15s',
              }}
              onClick={() => handleColorSelect(c)}
              title={c}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />
          ))}
          <div style={{ width: 1, background: 'rgba(0,0,0,0.1)', margin: '0 4px' }} />
          {NODE_SIZES.map((s, i) => (
            <button
              key={i}
              onClick={() => handleSizeSelect(i)}
              style={{
                padding: '2px 8px',
                borderRadius: 6,
                border: sizeIndex === i ? '1px solid #7B2FF7' : '1px solid transparent',
                background: sizeIndex === i ? 'rgba(123,47,247,0.1)' : 'transparent',
                color: '#1a1a2e',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {s.label}
            </button>
          ))}
          <button
            onClick={handleDeleteNode}
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: 'rgba(255,77,79,0.15)',
              border: '1px solid rgba(255,77,79,0.2)',
              color: '#FF4D4F',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 4,
              transition: 'background 0.2s',
            }}
            title="Удалить узел"
          >
            <Trash size={12} />
          </button>
        </div>
      )}
    </div>
  );
});

CustomNode.displayName = 'CustomNode';

// ==================== ПАНЕЛЬ ИНСТРУМЕНТОВ – теперь с выдвижением ====================
const Toolbar: React.FC<{
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCenter: () => void;
  onResetZoom: () => void;
  onExportJSON: () => void;
  onImportJSON: () => void;
  onAddNode: () => void;
  onToggleFullscreen: () => void;
  onExportPNG: () => void;
  onExportPDF: () => void;
  isFullscreen: boolean;
  onToggleHelp: () => void;
  showHelp: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
}> = ({ onZoomIn, onZoomOut, onCenter, onResetZoom, onExportJSON, onImportJSON, onAddNode, onToggleFullscreen, onExportPNG, onExportPDF, isFullscreen, onToggleHelp, showHelp, expanded, onToggleExpanded }) => {

  const btnStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: '12px',
    padding: '8px 14px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#1a1a2e',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  };

  if (!expanded) {
    return (
      <button
        onClick={onToggleExpanded}
        style={{
          ...btnStyle,
          marginBottom: '16px',
          width: 'fit-content',
          borderRadius: '12px',
          justifyContent: 'center',
        }}
        title="Развернуть панель инструментов"
      >
        <Menu size={18} /> Инструменты
      </button>
    );
  }

  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      padding: '8px 16px',
      background: 'rgba(255,255,255,0.5)',
      backdropFilter: 'blur(24px)',
      borderRadius: '20px',
      marginBottom: '16px',
      alignItems: 'center',
      border: '1px solid rgba(255,255,255,0.4)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
      zIndex: 1,
      width: 'fit-content',
      flexWrap: 'wrap',
    }}>
      <button onClick={onToggleExpanded} style={{ ...btnStyle, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)' }}>
        <ChevronLeft size={18} />
      </button>
      <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.08)' }} />
      <button onClick={onZoomIn} style={btnStyle}><ZoomIn size={16} /></button>
      <button onClick={onZoomOut} style={btnStyle}><ZoomOut size={16} /></button>
      <button onClick={onCenter} style={btnStyle}><Maximize size={16} /></button>
      <button onClick={onResetZoom} style={btnStyle}><Minus size={16} /></button>
      <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.08)' }} />
      <button onClick={onAddNode} style={btnStyle}><Plus size={16} /> Узел</button>
      <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.08)' }} />
      <button onClick={onExportJSON} style={btnStyle}><FileJson size={16} /></button>
      <button onClick={onImportJSON} style={btnStyle}><Upload size={16} /></button>
      <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.08)' }} />
      <button onClick={onExportPNG} style={btnStyle}><Download size={16} /> PNG</button>
      <button onClick={onExportPDF} style={btnStyle}><Download size={16} /> PDF</button>
      <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.08)' }} />
      <button onClick={onToggleFullscreen} style={btnStyle}>{isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}</button>
      <button
        onClick={onToggleHelp}
        style={{
          ...btnStyle,
          background: showHelp ? 'rgba(123,47,247,0.15)' : btnStyle.background,
          borderColor: showHelp ? '#7B2FF7' : 'rgba(255,255,255,0.5)',
          color: showHelp ? '#7B2FF7' : '#1a1a2e',
        }}
        title="Помощь"
      >
        <HelpCircle size={16} />
      </button>
    </div>
  );
};

// ==================== ОСНОВНОЙ КОМПОНЕНТ ====================
const MindMapEditorInner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const reactFlowInstance = useReactFlow();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [toolbarExpanded, setToolbarExpanded] = useState(false); // состояние панели инструментов

  const [edgeContextMenu, setEdgeContextMenu] = useState<{
    edgeId: string;
    x: number;
    y: number;
    stroke: string;
    strokeWidth: number;
    localStrokeWidth: number;
    onDelete?: () => void;
  } | null>(null);

  const adaptEdgeMenuPosition = (x: number, y: number, menuWidth: number, menuHeight: number) => {
    let newX = x;
    let newY = y;
    if (x + menuWidth > window.innerWidth - 20) newX = window.innerWidth - menuWidth - 20;
    if (y + menuHeight > window.innerHeight - 20) newY = y - menuHeight - 10;
    if (newY < 10) newY = 10;
    return { x: newX, y: newY };
  };

  const deleteEdge = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter(e => e.id !== edgeId));
    setEdgeContextMenu(null);
    showToast('Связь удалена', 'success');
  }, [setEdges, showToast]);

  const toggleFullscreen = useCallback(async () => {
    if (!exportRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await exportRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
      showToast('Не удалось переключить полноэкранный режим', 'error');
    }
  }, [exportRef, showToast]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const updateNodeLabel = useCallback((nodeId: string, newLabel: string) => {
    setNodes((nds) => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, label: newLabel } } : n));
  }, [setNodes]);

  const updateNodeColor = useCallback((nodeId: string, color: string) => {
    setNodes((nds) => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, backgroundColor: color } } : n));
  }, [setNodes]);

  const updateNodeSize = useCallback((nodeId: string, sizeIndex: number) => {
    setNodes((nds) => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, sizeIndex } } : n));
  }, [setNodes]);

  const deleteNode = useCallback((nodeId: string) => {
    if (nodeId === 'root') return;
    setNodes((nds) => nds.filter(n => n.id !== nodeId));
    setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNodeId(null);
  }, [setNodes, setEdges]);

  const updateEdgeLabel = useCallback((edgeId: string, label: string) => {
    setEdges((eds) => eds.map(e => e.id === edgeId ? { ...e, data: { ...e.data, label } } : e));
  }, [setEdges]);

  const updateEdgeStyle = useCallback((edgeId: string, stroke: string, strokeWidth: number) => {
    setEdges((eds) => eds.map(e => e.id === edgeId ? {
      ...e,
      style: { ...e.style, stroke, strokeWidth },
      data: { ...e.data, stroke, strokeWidth }
    } : e));
    setEdgeContextMenu(prev => prev && prev.edgeId === edgeId ? { ...prev, stroke, strokeWidth, localStrokeWidth: strokeWidth } : prev);
  }, [setEdges]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => setSelectedNodeId(node.id), []);
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setEdgeContextMenu(null);
  }, []);

  const onConnect = useCallback((params: Connection) => {
    const sourceHandle = params.sourceHandle || 'right-source';
    const targetHandle = params.targetHandle || 'left-target';
    const sourceNode = nodes.find(n => n.id === params.source);
    const color = sourceNode?.data?.backgroundColor || '#7B2FF7';
    const edgeId = `edge-${params.source}-${sourceHandle}-${params.target}-${targetHandle}-${Date.now()}`;

    setEdges((eds) => addEdge({
      ...params,
      id: edgeId,
      type: 'custom',
      animated: false,
      style: { stroke: color, strokeWidth: 2 },
      markerEnd: undefined,
      sourceHandle,
      targetHandle,
      data: {
        label: '',
        stroke: color,
        strokeWidth: 2,
        onLabelChange: updateEdgeLabel,
        onStyleChange: updateEdgeStyle,
        onDeleteEdge: deleteEdge,
      }
    }, eds));
  }, [setEdges, nodes, updateEdgeLabel, updateEdgeStyle, deleteEdge]);

  const onEdgeUpdate = useCallback((oldEdge: Edge, newConnection: Connection) => {
    setEdges((eds) => {
      const newEdges = eds.filter(e => e.id !== oldEdge.id);
      const sourceHandle = newConnection.sourceHandle || oldEdge.sourceHandle || 'right-source';
      const targetHandle = newConnection.targetHandle || oldEdge.targetHandle || 'left-target';
      const newEdge = {
        ...oldEdge,
        source: newConnection.source!,
        target: newConnection.target!,
        sourceHandle,
        targetHandle,
        id: `edge-${newConnection.source}-${sourceHandle}-${newConnection.target}-${targetHandle}-${Date.now()}`,
      };
      return addEdge(newEdge, newEdges);
    });
  }, [setEdges]);

  const onEdgeUpdateEnd = useCallback(() => { }, []);

  const loadMindMap = async () => {
    try {
      const res = await getMindMap(id!);
      setName(res.data.name);
      setDescription(res.data.description || '');

      const loadedNodes = res.data.nodes.map((n: any) => ({
        id: n.id,
        type: 'custom',
        position: n.position,
        data: {
          label: n.data?.label || 'Узел',
          backgroundColor: n.style?.backgroundColor || n.data?.backgroundColor || '#7B2FF7',
          sizeIndex: n.data?.sizeIndex ?? 1,
          onLabelChange: updateNodeLabel,
          onColorChange: updateNodeColor,
          onSizeChange: updateNodeSize,
          onDelete: deleteNode,
        }
      }));
      setNodes(loadedNodes);

      const loadedEdges = res.data.edges.map((e: any) => {
        let sourceHandle = e.sourceHandle || 'right-source';
        let targetHandle = e.targetHandle || 'left-target';
        return {
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle,
          targetHandle,
          type: 'custom',
          animated: false,
          style: { stroke: e.style?.stroke || '#7B2FF7', strokeWidth: e.style?.strokeWidth || 2 },
          markerEnd: undefined,
          data: {
            label: e.data?.label || '',
            stroke: e.style?.stroke || '#7B2FF7',
            strokeWidth: e.style?.strokeWidth || 2,
            onLabelChange: updateEdgeLabel,
            onStyleChange: updateEdgeStyle,
            onDeleteEdge: deleteEdge,
          }
        };
      });
      setEdges(loadedEdges);

      setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 100);
    } catch (err) {
      console.error('Ошибка загрузки:', err);
      showToast('Не удалось загрузить карту', 'error');
    }
  };

  useEffect(() => {
    if (id && id !== 'new') loadMindMap();
  }, [id]);

  const addNode = () => {
    const newNodeId = `node-${Date.now()}`;
    const viewport = reactFlowInstance.getViewport();
    const wrapper = exportRef.current;
    let centerX = 100, centerY = 100;
    if (wrapper) {
      centerX = (wrapper.clientWidth / 2 - 50) / viewport.zoom - viewport.x / viewport.zoom;
      centerY = (wrapper.clientHeight / 2 - 20) / viewport.zoom - viewport.y / viewport.zoom;
    }
    const randomColor = presetColors[Math.floor(Math.random() * presetColors.length)];
    const newNode: Node<CustomNodeData> = {
      id: newNodeId,
      type: 'custom',
      data: {
        label: 'Новый узел',
        backgroundColor: randomColor,
        sizeIndex: 1,
        onLabelChange: updateNodeLabel,
        onColorChange: updateNodeColor,
        onSizeChange: updateNodeSize,
        onDelete: deleteNode,
      },
      position: { x: centerX + (Math.random() - 0.5) * 50, y: centerY + (Math.random() - 0.5) * 50 }
    };
    setNodes((nds) => [...nds, newNode]);
    setTimeout(() => {
      const nodeElement = document.querySelector(`[data-node-id="${newNodeId}"]`);
      nodeElement?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
      reactFlowInstance.fitView({ padding: 0.3 });
    }, 150);
  };

  const saveMindMap = async () => {
    if (!name.trim()) { showToast('Введите название карты', 'error'); return; }
    setLoading(true);
    try {
      const data = {
        name,
        description,
        nodes: nodes.map(({ id, data, position }) => ({
          id,
          data: {
            label: (data as CustomNodeData).label,
            backgroundColor: (data as CustomNodeData).backgroundColor || '#7B2FF7',
            sizeIndex: (data as CustomNodeData).sizeIndex ?? 1,
          },
          position,
          style: { backgroundColor: (data as CustomNodeData).backgroundColor || '#7B2FF7' }
        })),
        edges: edges.map(({ id, source, target, sourceHandle, targetHandle, style, data }) => ({
          id, source, target,
          sourceHandle: sourceHandle || 'right-source',
          targetHandle: targetHandle || 'left-target',
          type: 'bezier',
          animated: false,
          style: { stroke: style?.stroke || '#7B2FF7', strokeWidth: style?.strokeWidth || 2 },
          data: { label: (data as any)?.label || '' }
        }))
      };
      if (id && id !== 'new') {
        await updateMindMap(id, data);
        showToast('Карта обновлена', 'success');
      } else {
        const res = await createMindMap(data);
        navigate(`/app/bi/mindmaps/${res.data._id}`);
        showToast('Карта создана', 'success');
      }
    } catch (err) {
      console.error('Ошибка сохранения:', err);
      showToast('Не удалось сохранить карту', 'error');
    } finally { setLoading(false); }
  };

  const exportToJSON = () => {
    const data = {
      name, description,
      nodes: nodes.map(({ id, data, position }) => ({
        id, label: (data as CustomNodeData).label,
        backgroundColor: (data as CustomNodeData).backgroundColor,
        sizeIndex: (data as CustomNodeData).sizeIndex,
        position
      })),
      edges: edges.map(({ id, source, target, sourceHandle, targetHandle, style, data }) => ({
        id, source, target, sourceHandle, targetHandle,
        stroke: style?.stroke, strokeWidth: style?.strokeWidth,
        label: (data as any)?.label
      }))
    };
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${name || 'mindmap'}.json`;
    a.click(); URL.revokeObjectURL(url);
    showToast('Экспорт JSON выполнен', 'success');
  };

  const importFromJSON = (jsonData: any) => {
    if (!jsonData) return;
    try {
      setName(jsonData.name || '');
      setDescription(jsonData.description || '');
      const importedNodes = jsonData.nodes.map((n: any) => ({
        id: n.id, type: 'custom', position: n.position,
        data: {
          label: n.label || 'Узел',
          backgroundColor: n.backgroundColor || '#7B2FF7',
          sizeIndex: n.sizeIndex ?? 1,
          onLabelChange: updateNodeLabel,
          onColorChange: updateNodeColor,
          onSizeChange: updateNodeSize,
          onDelete: deleteNode,
        }
      }));
      setNodes(importedNodes);
      const importedEdges = jsonData.edges.map((e: any) => ({
        id: e.id, source: e.source, target: e.target,
        sourceHandle: e.sourceHandle || 'right-source',
        targetHandle: e.targetHandle || 'left-target',
        type: 'custom', animated: false,
        style: { stroke: e.stroke || '#7B2FF7', strokeWidth: e.strokeWidth || 2 },
        markerEnd: undefined,
        data: {
          label: e.label || '',
          stroke: e.stroke || '#7B2FF7',
          strokeWidth: e.strokeWidth || 2,
          onLabelChange: updateEdgeLabel,
          onStyleChange: updateEdgeStyle,
          onDeleteEdge: deleteEdge,
        }
      }));
      setEdges(importedEdges);
      setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 100);
      showToast('Импорт выполнен', 'success');
    } catch (err) {
      console.error('Ошибка импорта:', err);
      showToast('Не удалось импортировать данные', 'error');
    }
  };

  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target?.result as string);
          importFromJSON(json);
        } catch (err) { showToast('Ошибка импорта JSON', 'error'); }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const exportToPNG = async () => {
    if (!exportRef.current) return;
    showToast('Подготовка PNG...', 'info');
    const currentViewport = reactFlowInstance.getViewport();
    const controls = exportRef.current.querySelectorAll('.react-flow__controls, .react-flow__minimap, .react-flow__panel');
    controls.forEach(el => (el as HTMLElement).style.display = 'none');
    reactFlowInstance.setNodes([...nodes]); reactFlowInstance.setEdges([...edges]);
    await new Promise(r => setTimeout(r, 300));
    reactFlowInstance.fitView({ padding: 0.1, duration: 0 });
    await new Promise(r => setTimeout(r, 1000));
    try {
      const result = await snapdom(exportRef.current, { scale: window.devicePixelRatio * 2, backgroundColor: '#f5f7fa', embedFonts: true });
      const img = await result.toPng();
      const link = document.createElement('a'); link.download = `${name || 'mindmap'}.png`; link.href = img.src; link.click();
      showToast('PNG успешно сохранён', 'success');
    } catch (err) { showToast('Ошибка экспорта PNG', 'error'); }
    finally {
      controls.forEach(el => (el as HTMLElement).style.display = '');
      reactFlowInstance.setViewport(currentViewport, { duration: 0 });
    }
  };

  const exportToPDF = async () => {
    if (!exportRef.current) return;
    showToast('Подготовка PDF...', 'info');
    const currentViewport = reactFlowInstance.getViewport();
    const controls = exportRef.current.querySelectorAll('.react-flow__controls, .react-flow__minimap, .react-flow__panel');
    controls.forEach(el => (el as HTMLElement).style.display = 'none');
    reactFlowInstance.setNodes([...nodes]); reactFlowInstance.setEdges([...edges]);
    await new Promise(r => setTimeout(r, 300));
    reactFlowInstance.fitView({ padding: 0.1, duration: 0 });
    await new Promise(r => setTimeout(r, 1000));
    try {
      const result = await snapdom(exportRef.current, { scale: window.devicePixelRatio * 2, backgroundColor: '#f5f7fa', embedFonts: true });
      const img = await result.toPng();
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth(); const pdfHeight = pdf.internal.pageSize.getHeight(); const margin = 10;
      const tempImg = new Image(); tempImg.src = img.src; await new Promise((resolve) => { tempImg.onload = resolve; });
      let imgWidth = pdfWidth - margin * 2; let imgHeight = (tempImg.height * imgWidth) / tempImg.width;
      if (imgHeight > pdfHeight - margin * 2) { imgHeight = pdfHeight - margin * 2; imgWidth = (tempImg.width * imgHeight) / tempImg.height; }
      const x = (pdfWidth - imgWidth) / 2; const y = (pdfHeight - imgHeight) / 2;
      pdf.addImage(img.src, 'PNG', x, y, imgWidth, imgHeight);
      pdf.save(`${name || 'mindmap'}.pdf`);
      showToast('PDF успешно сохранён', 'success');
    } catch (err) { showToast('Ошибка экспорта PDF', 'error'); }
    finally {
      controls.forEach(el => (el as HTMLElement).style.display = '');
      reactFlowInstance.setViewport(currentViewport, { duration: 0 });
    }
  };

  const zoomIn = () => reactFlowInstance.zoomIn();
  const zoomOut = () => reactFlowInstance.zoomOut();
  const centerView = () => reactFlowInstance.fitView({ padding: 0.2 });
  const resetZoom = () => reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });

  const toggleHelp = () => setShowHelp(prev => !prev);
  const toggleToolbar = () => setToolbarExpanded(prev => !prev);

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const { edgeId, x, y, stroke, strokeWidth, onDelete } = e.detail;
      const adapted = adaptEdgeMenuPosition(x, y, 260, 330);
      setEdgeContextMenu({
        edgeId,
        x: adapted.x,
        y: adapted.y,
        stroke,
        strokeWidth,
        localStrokeWidth: strokeWidth,
        onDelete: () => { onDelete?.(); deleteEdge(edgeId); }
      });
    };
    window.addEventListener('openEdgeContextMenu', handler as EventListener);
    return () => window.removeEventListener('openEdgeContextMenu', handler as EventListener);
  }, [deleteEdge, adaptEdgeMenuPosition]);

  const edgeTypes = useMemo(() => ({ custom: CustomEdge }), []);
  const nodeTypes: NodeTypes = useMemo(() => ({
    custom: (props: any) => <CustomNode {...props} selected={selectedNodeId === props.id} />
  }), [selectedNodeId]);

  const reactFlowProps = {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onEdgeUpdate,
    onEdgeUpdateEnd,
    onNodeClick,
    onPaneClick,
    nodeTypes,
    edgeTypes,
    fitView: true,
    fitViewOptions: { padding: 0.2 },
    defaultEdgeOptions: {
      type: 'custom',
      animated: false,
      style: { stroke: '#7B2FF7', strokeWidth: 2 },
      markerEnd: undefined,
    },
    snapToGrid: true,
    snapGrid: [10, 10] as [number, number],
    minZoom: 0.1,
    maxZoom: 2,
    zoomOnScroll: true,
    panOnScroll: true,
    deleteKeyCode: ['Delete', 'Backspace'],
    edgesUpdatable: true,
    edgesFocusable: true,
    connectionRadius: 50,
    connectionLineType: ConnectionLineType.Bezier,
  };

  return (
    <div style={styles.container}>
      <div style={styles.backgroundDecor} />
      
      {!isFullscreen && (
        <div style={styles.header}>
          <button onClick={() => navigate('/app/bi')} style={styles.backButton}>
            <ArrowLeft size={18} /> Назад
          </button>
          <input
            type="text"
            placeholder="Название карты"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.titleInput}
          />
          <textarea
            placeholder="Описание"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={styles.descInput}
            rows={1}
          />
          <div style={styles.actionButtons}>
            <button onClick={addNode} style={styles.addNodeButton}>+ Узел</button>
            <button onClick={saveMindMap} disabled={loading} style={styles.saveButton}>
              <Save size={18} /> {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}

      <Toolbar
        onZoomIn={zoomIn} onZoomOut={zoomOut} onCenter={centerView} onResetZoom={resetZoom}
        onExportJSON={exportToJSON} onImportJSON={handleImportClick} onAddNode={addNode}
        onToggleFullscreen={toggleFullscreen} onExportPNG={exportToPNG} onExportPDF={exportToPDF}
        isFullscreen={isFullscreen}
        onToggleHelp={toggleHelp}
        showHelp={showHelp}
        expanded={toolbarExpanded}
        onToggleExpanded={toggleToolbar}
      />

      <div
        ref={exportRef}
        style={{
          ...styles.flowWrapper,
          ...(isFullscreen ? {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, overflow: 'auto',
            backgroundColor: 'white'
          } : {})
        }}
      >
        <ReactFlow {...reactFlowProps}>
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="rgba(0,0,0,0.04)" />
          <Controls showInteractive={false} style={{ backgroundColor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(12px)', borderRadius: 16 }} />
          <MiniMap
            nodeColor={(node) => (node as any).data?.backgroundColor || '#7B2FF7'}
            maskColor="rgba(0,0,0,0.04)"
            style={{ backgroundColor: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(12px)', borderRadius: 16 }}
          />
          {showHelp && (
            <Panel position="top-right" style={{ pointerEvents: 'auto' }}>
              <div style={styles.helpPanel}>
                <div>✏️ Двойной клик – редактировать текст</div>
                <div>🖱️ Выделите узел – панель цвета/размера</div>
                <div>🔗 Перетащите ручку для связи</div>
                <div>📌 Перетащите конец связи в пустоту для открепления</div>
                <div>🔵 ПКМ по связи – изменить цвет/толщину/удалить</div>
              </div>
            </Panel>
          )}
        </ReactFlow>
      </div>

      {edgeContextMenu && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: edgeContextMenu.y,
            left: edgeContextMenu.x,
            zIndex: 100000,
            pointerEvents: 'auto',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            border: '1px solid rgba(255,255,255,0.6)',
            width: '260px',
            animation: 'fadeIn 0.2s ease'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: edgeContextMenu.stroke }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>Стиль связи</span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, marginBottom: 8, color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Цвет</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {presetColors.slice(0, 12).map(color => (
                <div
                  key={color}
                  style={{
                    width: 30, height: 30, borderRadius: 8, backgroundColor: color,
                    border: color === edgeContextMenu.stroke ? '2px solid #7B2FF7' : '1px solid rgba(0,0,0,0.1)',
                    cursor: 'pointer', transition: 'transform 0.15s',
                  }}
                  onClick={() => updateEdgeStyle(edgeContextMenu.edgeId, color, edgeContextMenu.strokeWidth)}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                />
              ))}
              <input
                type="color"
                value={edgeContextMenu.stroke}
                style={{ width: 30, height: 30, borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.1)', background: 'none' }}
                onChange={(e) => updateEdgeStyle(edgeContextMenu.edgeId, e.target.value, edgeContextMenu.strokeWidth)}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, marginBottom: 8, color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>
              Толщина: {edgeContextMenu.localStrokeWidth}px
            </div>
            <input
              type="range"
              min="1" max="10" step="1"
              value={edgeContextMenu.localStrokeWidth}
              onChange={(e) => {
                const newWidth = parseInt(e.target.value);
                setEdgeContextMenu(prev => prev ? { ...prev, localStrokeWidth: newWidth } : prev);
                updateEdgeStyle(edgeContextMenu.edgeId, edgeContextMenu.stroke, newWidth);
              }}
              style={{ width: '100%', accentColor: '#7B2FF7', cursor: 'pointer' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setEdgeContextMenu(null)}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0,0,0,0.1)', borderRadius: '30px', padding: '8px',
                color: '#666', cursor: 'pointer', fontSize: 13, fontWeight: 500
              }}
            >
              Закрыть
            </button>
            <button
              onClick={() => {
                edgeContextMenu.onDelete?.();
                setEdgeContextMenu(null);
              }}
              style={{
                flex: 1, background: '#FF4D4F', border: 'none', borderRadius: '30px', padding: '8px',
                color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                boxShadow: '0 4px 12px rgba(255,77,79,0.3)'
              }}
            >
              <Trash2 size={14} /> Удалить
            </button>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .custom-node:hover .react-flow__handle {
          opacity: 1 !important;
          transform: scale(1.3);
        }
        .react-flow__edge-path { stroke-linecap: round !important; }
        .react-flow__controls { box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important; border-radius: 16px !important; overflow: hidden !important; }
        .react-flow__minimap { border-radius: 16px !important; box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important; }
      `}</style>
    </div>
  );
};

const MindMapEditor: React.FC = () => {
  return (
    <BILayout>
      <ReactFlowProvider>
        <MindMapEditorInner />
      </ReactFlowProvider>
    </BILayout>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 100px)',
    padding: '20px',
    position: 'relative',
    background: 'transparent',
  },
  backgroundDecor: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 30%, rgba(123,47,247,0.06), transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,229,255,0.06), transparent 40%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  header: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(20px)',
    padding: '12px 20px',
    borderRadius: '24px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.03)',
    border: '1px solid rgba(255,255,255,0.5)',
    zIndex: 1,
  },
  backButton: {
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: '40px',
    padding: '8px 20px',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    fontWeight: 500,
    boxShadow: '0 4px 10px rgba(0,0,0,0.03)',
  },
  titleInput: {
    flex: 1,
    padding: '8px 18px',
    borderRadius: '40px',
    border: '1px solid rgba(0,0,0,0.08)',
    fontSize: '16px',
    background: 'rgba(255,255,255,0.8)',
    minWidth: '200px',
    outline: 'none',
    transition: 'border 0.2s, box-shadow 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
  },
  descInput: {
    flex: 2,
    padding: '8px 18px',
    borderRadius: '40px',
    border: '1px solid rgba(0,0,0,0.08)',
    fontSize: '14px',
    background: 'rgba(255,255,255,0.8)',
    minWidth: '250px',
    outline: 'none',
    transition: 'border 0.2s, box-shadow 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
  },
  actionButtons: {
    display: 'flex',
    gap: '10px',
    marginLeft: 'auto',
  },
  addNodeButton: {
    background: 'rgba(40, 167, 69, 0.9)',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    padding: '8px 18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 500,
    transition: 'all 0.2s',
    boxShadow: '0 6px 14px rgba(40, 167, 69, 0.2)',
    backdropFilter: 'blur(8px)',
  },
  saveButton: {
    background: 'linear-gradient(135deg, #7B2FF7 0%, #00E5FF 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    padding: '8px 20px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 500,
    transition: 'opacity 0.2s, box-shadow 0.2s',
    boxShadow: '0 8px 20px rgba(123, 47, 247, 0.4)',
  },
  flowWrapper: {
    flex: 1,
    background: 'transparent',
    overflow: 'hidden',
    position: 'relative',
    borderRadius: '24px',
    zIndex: 1,
  },
  helpPanel: {
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(24px)',
    color: '#1a1a2e',
    padding: '12px 18px',
    borderRadius: '20px',
    fontSize: '12px',
    lineHeight: '1.8',
    boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
    pointerEvents: 'auto',
    border: '1px solid rgba(255,255,255,0.6)',
    fontWeight: 500,
  },
};

export default MindMapEditor;